import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ThemeContext } from 'styled-components';

import tracker_cam from 'assets/images/tracker-cam.png';
import tracker_cam_ia from 'assets/images/tracker-cam-ia.png';

import {
  ActionPortal,
  ButtonWithIcon,
  Checkbox,
  DefaultActionButtons,
  Form,
  Icon,
  MaskedInput,
  Step,
  Text,
} from 'components';

import { Gray, Purple, Orange } from 'constants/colors';
import { StepPropTypes } from 'constants/propTypes';

const formValidationSchema = yup.object().shape({
  conteleCamCount: yup.number().integer().min(0),
  conteleCamIaCount: yup.number().integer().min(0),
  acceptTerms: yup.boolean().oneOf([true], 'Você deve aceitar os termos'),
});

export default function CameraTypes(props) {
  const theme = useContext(ThemeContext);
  const { onSubmit, onCancel, initialValues: defaultValues } = props;

  const {
    control,
    formState,
    handleSubmit,
    watch,
    setValue,
    trigger,
    register,
  } = useForm({
    mode: 'all',
    defaultValues: {
      conteleCamCount: defaultValues?.cameraTypes?.conteleCamCount || 0,
      conteleCamIaCount: defaultValues?.cameraTypes?.conteleCamIaCount || 0,
      acceptTerms: defaultValues?.cameraTypes?.acceptTerms || false,
    },
    resolver: yupResolver(formValidationSchema),
  });

  const conteleCamCount = watch('conteleCamCount');
  const conteleCamIaCount = watch('conteleCamIaCount');

  const { isValid } = formState;

  const submitForm = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit)();
  };

  const totalContractedCameras = defaultValues?.vehicleManager?.conteleCamerasCount;
  const totalSelectedCameras = (Number(conteleCamCount) || 0) + (Number(conteleCamIaCount) || 0);
  const isQuantityValid = totalSelectedCameras === totalContractedCameras;
  const canAddMore = totalSelectedCameras < totalContractedCameras;
  const isFormValid = isValid && isQuantityValid && watch('acceptTerms');

  const baseCameraFeatures = [
    { label: 'Possibilidade de visualização em tempo real', isAvailable: true },
    { label: 'Acesso às imagens gravadas', isAvailable: true },
    { label: 'Identificação do Motorista com I.A', isAvailable: true },
    { label: 'Eventos do Motorista com I.A', isAvailable: false },
    { label: 'Eventos da Via com I.A', isAvailable: false },
  ];

  const getCameraFeatures = (type) => baseCameraFeatures.map((feature) => {
    if (type === 'ia') {
      if (feature.label === 'Identificação do Motorista com I.A') {
        return { ...feature, isAvailable: true };
      }
      if (feature.label === 'Eventos do Motorista com I.A') {
        return { ...feature, isAvailable: true };
      }
      if (feature.label === 'Eventos da Via com I.A') {
        return { ...feature, isAvailable: true };
      }
    }
    return feature;
  });

  return (
    <div style={{ height: '100%', backgroundColor: Gray[200] }}>
      <Step
        headerTitle="Escolha a quantidade de cada modelo das câmeras"
        style={{
          backgroundColor: Gray[200],
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'auto',
        }}
      >
        <Form onKeyPress={(e) => e.key === 'Enter' && submitForm(e)}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '1100px',
              border: `1px solid ${Gray[250]}`,
              gap: '8px',
              borderRadius: '5px',
              backgroundColor: Gray[100],
              padding: '20px',
              minHeight: '600px',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'space-evenly',
              }}
            >
              <section
                style={{
                  width: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={tracker_cam}
                    alt="contele-cam"
                    style={{ width: '100%', objectFit: 'contain' }}
                  />
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    Contele Cam PLUS
                  </p>
                  <div
                    style={{
                      backgroundColor: theme.newPalette.background.success[200],
                      color: theme.newPalette.background.success[100],
                      borderRadius: 4,
                      padding: '2px 10px',
                      fontWeight: 500,
                      margin: '10px',
                    }}
                  >
                    Em média 40% off
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: 1,
                      backgroundColor: theme.newPalette.background.gray[300],
                      marginBottom: '20px',
                      marginTop: '12px',
                    }}
                  />
                </section>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '0px 32px',
                    gap: '10px',
                  }}
                >
                  {getCameraFeatures('basic').map((item) => (
                    <p
                      style={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        textDecoration: item.isAvailable
                          ? 'none'
                          : 'line-through',
                        fontSize: 14,
                      }}
                    >
                      <CheckIcon value={item.isAvailable} />
                      <span>{item.label}</span>
                    </p>
                  ))}
                </article>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: 32,
                  }}
                >
                  <Text
                    color={Gray[500]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="600"
                  >
                    Quantidade de veículos com Contele Cam PLUS
                  </Text>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <ButtonWithIcon
                      icon="minus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if ((Number(conteleCamCount) || 0) === 0) {
                          return;
                        }
                        setValue('conteleCamCount', (Number(conteleCamCount) || 1) - 1);
                        trigger();
                      }}
                    />
                    <MaskedInput
                      control={control}
                      name="conteleCamCount"
                      placeholder="0"
                      format="numeric"
                      containerWidth="100%"
                      style={{
                        border: `1px solid ${Gray[250]}`,
                        borderRadius: '5px',
                      }}
                    />
                    <ButtonWithIcon
                      icon="plus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if (!canAddMore) {
                          return;
                        }
                        setValue('conteleCamCount', (Number(conteleCamCount) || 0) + 1);
                        trigger();
                      }}
                      disabled={!canAddMore}
                    />
                  </div>
                </div>
              </section>

              <section
                style={{
                  width: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <section
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={tracker_cam_ia}
                    alt="contele-cam-ia"
                    style={{ width: '100%', objectFit: 'contain' }}
                  />
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    Contele Cam MAX
                  </p>
                  <div
                    style={{
                      height: '32px',
                      margin: '10px',
                    }}
                  />
                  <div
                    style={{
                      width: '100%',
                      height: 1,
                      backgroundColor: theme.newPalette.background.gray[300],
                      marginBottom: '20px',
                      marginTop: '12px',
                    }}
                  />
                </section>
                <article
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: '0px 32px',
                    gap: '10px',
                  }}
                >
                  {getCameraFeatures('ia').map((item) => (
                    <p
                      style={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        fontSize: 14,
                      }}
                    >
                      <CheckIcon value />
                      <span>{item.label}</span>
                    </p>
                  ))}
                </article>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: 32,
                  }}
                >
                  <Text
                    color={Gray[500]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="600"
                  >
                    Quantidade de veículos com Contele Cam MAX
                  </Text>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <ButtonWithIcon
                      icon="minus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if ((Number(conteleCamIaCount) || 0) === 0) {
                          return;
                        }
                        setValue('conteleCamIaCount', (Number(conteleCamIaCount) || 1) - 1);
                        trigger();
                      }}
                    />
                    <MaskedInput
                      control={control}
                      name="conteleCamIaCount"
                      placeholder="0"
                      format="numeric"
                      containerWidth="100%"
                      style={{
                        border: `1px solid ${Gray[250]}`,
                        borderRadius: '5px',
                      }}
                      maxLength="3"
                      min={0}
                    />
                    <ButtonWithIcon
                      icon="plus"
                      customBackgroundColor={Gray[100]}
                      border={`1px solid ${Gray[250]}`}
                      customIconColor={Gray[400]}
                      height="32px"
                      iconOptions={{ width: '14px', height: '14px' }}
                      onClick={() => {
                        if (!canAddMore) {
                          return;
                        }
                        setValue('conteleCamIaCount', (Number(conteleCamIaCount) || 0) + 1);
                        trigger();
                      }}
                      disabled={!canAddMore}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: Gray[200],
              borderRadius: '5px',
              padding: '20px',
            }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Icon
                  color={Gray[400]}
                  icon="exclamationCircle"
                  style={{ width: '16px', height: '16px' }}
                />
                <Text
                  color={Gray[400]}
                  fontSize="16px"
                  lineHeight="19px"
                  fontWeight="700"
                >
                  Instalação das câmeras
                </Text>
              </div>
              <Text
                color={Gray[400]}
                fontSize="14px"
                lineHeight="19px"
                fontWeight="400"
              >
                Você receberá o kit do equipamento já configurado com um manual de instalação. A responsabilidade pela instalação, retirada e/ou substituição é da empresa CONTRATANTE. A instalação precisa ser feita por um profissional especializado, sendo que todo o suporte necessário será fornecido pela Contele. Não há cobrança de instalação por parte da Contele.
              </Text>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  type="checkbox"
                  control={control}
                  name="acceptTerms"
                  id="acceptTerms"
                  innerRef={register({
                    required: true,
                  })}
                />
                <Text
                  color={Gray[400]}
                  fontSize="14px"
                  lineHeight="19px"
                  fontWeight="400"
                  textAlign="center"
                >
                  Sim, estou ciente.
                </Text>
              </div>
            </div>

            {!isFormValid && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                backgroundColor: Orange[100],
                borderRadius: '5px',
                padding: '20px',
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Icon
                    color={Orange[400]}
                    icon="exclamationTriangle"
                    style={{ width: '16px', height: '16px' }}
                  />
                  <Text
                    color={Orange[400]}
                    fontSize="16px"
                    lineHeight="19px"
                    fontWeight="700"
                  >
                    Requisitos para Prosseguir
                  </Text>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
                >
                  {!isQuantityValid && (
                    <Text
                      color={Orange[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                    >
                      • Você precisa selecionar exatamente {totalContractedCameras} câmeras (atualmente selecionadas: {totalSelectedCameras})
                    </Text>
                  )}
                  {!watch('acceptTerms') && (
                    <Text
                      color={Orange[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                    >
                      • É necessário aceitar os termos de instalação das câmeras
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
          <ActionPortal>
            <DefaultActionButtons
              theme={theme}
              isValid={isFormValid}
              backButtonStyle={{
                iconPosition: 'left',
                border: 'none',
                customBackgroundColor: 'transparent',
                customIconColor: Gray[400],
                customTextColor: Gray[400],
                title: 'Passo Anterior',
              }}
              nextButtonStyle={{
                customBackgroundColor: Purple[500],
                customIconColor: Gray[100],
                customTextColor: Gray[100],
                title: 'Próximo Passo',
              }}
              onCancel={onCancel}
              onSubmit={() => handleSubmit(onSubmit)()}
            />
          </ActionPortal>
        </Form>
      </Step>
    </div>
  );
}

export const CheckIcon = ({ value }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {value ? (
      <path
        d="M14 7.32886C14 11.1949 10.866 14.3289 7 14.3289C3.134 14.3289 0 11.1949 0 7.32886C0 3.46285 3.134 0.328857 7 0.328857C10.866 0.328857 14 3.46285 14 7.32886ZM6.19031 11.0353L11.3839 5.84175C11.5602 5.6654 11.5602 5.37944 11.3839 5.20309L10.7452 4.56442C10.5688 4.38804 10.2829 4.38804 10.1065 4.56442L5.87097 8.79993L3.8935 6.82246C3.71714 6.6461 3.43119 6.6461 3.2548 6.82246L2.61614 7.46112C2.43978 7.63748 2.43978 7.92343 2.61614 8.09979L5.55162 11.0353C5.728 11.2117 6.01393 11.2117 6.19031 11.0353Z"
        fill="#12B76A"
      />
    ) : (
      <path
        d="M7 0.328857C3.13306 0.328857 0 3.46192 0 7.32886C0 11.1958 3.13306 14.3289 7 14.3289C10.8669 14.3289 14 11.1958 14 7.32886C14 3.46192 10.8669 0.328857 7 0.328857ZM10.4323 9.16636C10.5649 9.29902 10.5649 9.51353 10.4323 9.6462L9.31452 10.7611C9.18185 10.8938 8.96734 10.8938 8.83468 10.7611L7 8.9095L5.1625 10.7611C5.02984 10.8938 4.81532 10.8938 4.68266 10.7611L3.56774 9.64337C3.43508 9.51071 3.43508 9.2962 3.56774 9.16353L5.41935 7.32886L3.56774 5.49136C3.43508 5.3587 3.43508 5.14418 3.56774 5.01152L4.68548 3.89378C4.81814 3.76112 5.03266 3.76112 5.16532 3.89378L7 5.74821L8.8375 3.8966C8.97016 3.76394 9.18468 3.76394 9.31734 3.8966L10.4351 5.01434C10.5677 5.147 10.5677 5.36152 10.4351 5.49418L8.58064 7.32886L10.4323 9.16636Z"
        fill="#B42318"
      />
    )}
  </svg>
);

CameraTypes.propTypes = StepPropTypes;
